import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, FileDownIcon } from 'lucide-react';
import clsx from 'clsx';
import {
  Bar,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { Button, Text } from '@/components/ui';
import { Select, SelectOption } from '@/components/ui/Select';
import { Spinner } from '@/components/Spinner';
import { useCompanies } from '@/hooks/useCompanies';
import { useContractOptions } from '@/hooks/useContractOptions';
import { useContractConsumption } from '@/hooks/useContractConsumption';
import { ContractConsumptionDashboard } from '@/lib/api/reports';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const PERIOD_MONTHS_BACK = 36;

export const fmtHours = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : `${v.toLocaleString('pt-BR')}h`;
export const fmtPercent = (v: number | null) =>
  v === null ? '-' : `${v.toLocaleString('pt-BR')}%`;
export const fmtDate = (iso: string | null) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : '-';
const monthLabel = (m: { year: number; month: number }) =>
  `${MONTHS[m.month - 1]}/${String(m.year).slice(2)}`;

function monthOptions(): SelectOption[] {
  const now = new Date();
  const options: SelectOption[] = [];
  for (let i = 0; i < PERIOD_MONTHS_BACK; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    options.push({ value, content: `${MONTHS[d.getMonth()]}/${d.getFullYear()}` });
  }
  return options;
}

export function ContractConsumptionPage() {
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState('all');
  // ?contractId= lets the portfolio page link straight to one contract
  const [searchParams] = useSearchParams();
  const [contractId, setContractId] = useState(searchParams.get('contractId') ?? '');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');

  const { companies } = useCompanies();
  const { contractOptions } = useContractOptions({ includeInactive: true, companyId });
  const periodOptions = useMemo(monthOptions, []);

  const companyOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', content: t('common.all') },
      ...companies.map((c) => ({ value: c.id, content: c.name })),
    ],
    [companies, t]
  );

  const filters = contractId
    ? { contractId, startMonth: startMonth || undefined, endMonth: endMonth || undefined }
    : null;
  const { data, isLoading, isExporting, exportPdf } = useContractConsumption(filters);

  return (
    <div className="gap-xl flex flex-1 flex-col">
      <header className="gap-sm flex w-full flex-wrap items-center justify-between">
        <div className="gap-2xs flex items-center">
          <Link to="/reports" className="text-neutral-muted hover:text-neutral-default">
            <ArrowLeftIcon size={20} />
          </Link>
          <Text as="h1" variant="heading">
            {t('reports.contractConsumption.title')}
          </Text>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={exportPdf}
          disabled={!data || isExporting}
          loading={isExporting}
        >
          <FileDownIcon size={20} />
          {t('reports.contractConsumption.exportPdf')}
        </Button>
      </header>

      <section className="border-transparent-dark-12 bg-base-white p-md gap-xs flex flex-wrap rounded-2xl border shadow-xs">
        <Select
          options={companyOptions}
          placeholder={t('reports.filters.companyPlaceholder')}
          width={240}
          value={companyId === 'all' ? '' : companyId}
          onValueChange={(v) => {
            setCompanyId(v || 'all');
            setContractId('');
          }}
        />
        <Select
          options={contractOptions}
          placeholder={t('reports.filters.contractPlaceholder')}
          width={280}
          value={contractId}
          onValueChange={(v) => setContractId(v || '')}
        />
        <Select
          options={periodOptions}
          placeholder={t('reports.contractConsumption.fromMonth')}
          width={150}
          value={startMonth}
          onValueChange={(v) => setStartMonth(v || '')}
        />
        <Select
          options={periodOptions}
          placeholder={t('reports.contractConsumption.toMonth')}
          width={150}
          value={endMonth}
          onValueChange={(v) => setEndMonth(v || '')}
        />
        {(startMonth || endMonth) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStartMonth('');
              setEndMonth('');
            }}
          >
            {t('reports.contractConsumption.defaultPeriod')}
          </Button>
        )}
      </section>

      {!contractId && <EmptyState text={t('reports.contractConsumption.selectContract')} />}
      {contractId && isLoading && !data && (
        <div className="py-2xl flex justify-center">
          <Spinner color="black" />
        </div>
      )}
      {data && (
        <div className={clsx('gap-md flex flex-col', isLoading && 'opacity-60')}>
          <Dashboard data={data} />
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="border-transparent-dark-12 bg-base-white py-2xl flex items-center justify-center rounded-2xl border border-dashed">
      <Text className="text-neutral-muted">{text}</Text>
    </div>
  );
}

export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={clsx(
        'bg-base-white border-transparent-dark-12 p-md rounded-2xl border shadow-xs',
        className
      )}
    >
      {children}
    </div>
  );
}

export function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="gap-2xs flex flex-col">
      <Text variant="label" size="xs" className="text-neutral-muted uppercase">
        {label}
      </Text>
      <Text variant="heading" size="md" strong>
        {value}
      </Text>
      {hint && (
        <Text variant="label" size="xs" className="text-neutral-muted">
          {hint}
        </Text>
      )}
    </Card>
  );
}

function projectionText(d: ContractConsumptionDashboard) {
  const { status, depletionDate } = d.projection;
  switch (status) {
    case 'NO_CONTRACTED_HOURS':
      return 'Contrato sem banco de horas: acompanhe pelo limite mensal.';
    case 'EXCEEDED':
      return 'As horas contratadas já foram esgotadas.';
    case 'NO_RECENT_CONSUMPTION':
      return 'Sem consumo nos últimos 3 meses fechados: não há projeção de esgotamento.';
    case 'NO_END_DATE':
      return `No ritmo atual, o saldo acaba por volta de ${fmtDate(depletionDate)}.`;
    case 'DEPLETES_BEFORE_END':
      return `No ritmo atual, o saldo acaba por volta de ${fmtDate(depletionDate)}, antes do fim do contrato (${fmtDate(d.contract.endDate)}).`;
    case 'LASTS_UNTIL_END':
      return `No ritmo atual, o saldo é suficiente até o fim do contrato (${fmtDate(d.contract.endDate)}).`;
  }
}

export function daysText(days: number | null) {
  if (days === null) return 'Sem data de fim';
  if (days < 0) return `Vencido há ${Math.abs(days)} dia(s)`;
  if (days === 0) return 'Vence hoje';
  return `${days} dia(s) até o fim`;
}

export const alertClasses = {
  critical: 'bg-error-soft-surface text-error-default',
  warning: 'bg-amber-50 text-amber-800',
  info: 'bg-neutral-soft-surface text-neutral-default',
};

function Dashboard({ data }: { data: ContractConsumptionDashboard }) {
  const { contract, summary, projection } = data;
  const percent = Math.min(100, Math.max(0, summary.percentUsed ?? 0));
  const chartData = data.monthly.map((m) => ({ ...m, label: monthLabel(m) }));

  return (
    <>
      <Card className="gap-md grid grid-cols-2 md:grid-cols-5">
        <Info label="Empresa" value={contract.companyName} />
        <Info label="Contrato" value={contract.number} />
        <Info
          label="Vigência"
          value={
            contract.endDate
              ? `${fmtDate(contract.startDate)} a ${fmtDate(contract.endDate)}`
              : `Desde ${fmtDate(contract.startDate)} (sem data de fim)`
          }
        />
        <Info
          label="Contratado / limite mensal"
          value={`${fmtHours(contract.contractedHours)} / ${fmtHours(contract.monthlyHoursLimit)}`}
        />
        <Info
          label="Período"
          value={`${monthLabel(data.period.start)} a ${monthLabel(data.period.end)}`}
        />
      </Card>

      <div className="gap-md grid grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Consumido (total)"
          value={fmtHours(summary.consumedHours)}
          hint={
            summary.percentUsed === null
              ? 'Contrato sem banco de horas'
              : `${fmtPercent(summary.percentUsed)} do contratado`
          }
        />
        <Kpi
          label="Saldo"
          value={fmtHours(summary.remainingHours)}
          hint={daysText(contract.daysToEnd)}
        />
        <Kpi
          label="Consumido no período"
          value={fmtHours(summary.periodConsumedHours)}
          hint={`${summary.periodTicketCount} ticket(s)`}
        />
        <Kpi
          label="Média mensal"
          value={fmtHours(summary.averageMonthlyHours)}
          hint={`${summary.monthsOverLimit} mês(es) acima do limite`}
        />
      </div>

      {(contract.excludeProactiveTickets || data.proactive.periodTicketCount > 0) && (
        <div className="gap-md grid grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="Monitoração proativa (período)"
            value={fmtHours(data.proactive.periodWorkedHours)}
            hint={`${data.proactive.periodTicketCount} ticket(s) de alertas do Zabbix`}
          />
          <Kpi
            label="Monitoração proativa (total)"
            value={fmtHours(data.proactive.totalWorkedHours)}
            hint={contract.excludeProactiveTickets ? 'Não debita do bolsão' : 'Debitado do bolsão'}
          />
        </div>
      )}

      {summary.percentUsed !== null && (
        <div>
          <div className="bg-neutral-subtle h-2.5 overflow-hidden rounded-full">
            <div
              className={clsx(
                'h-full rounded-full',
                projection.status === 'EXCEEDED' ? 'bg-error-default' : 'bg-brand-default'
              )}
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="mt-4xs flex justify-between">
            <Text variant="label" size="xs" className="text-neutral-muted">
              0h
            </Text>
            <Text variant="label" size="xs" className="text-neutral-muted">
              {fmtHours(contract.contractedHours)}
            </Text>
          </div>
        </div>
      )}

      <div className="border-brand-strong bg-neutral-soft-surface px-sm py-xs rounded-md border-l-4">
        <Text>
          <strong>Projeção:</strong> {projectionText(data)}
          {projection.burnRateHours > 0 &&
            ` Ritmo considerado: ${fmtHours(projection.burnRateHours)}/mês (média dos últimos 3 meses fechados).`}
        </Text>
      </div>

      {data.alerts.length > 0 && (
        <div className="gap-2xs flex flex-col">
          {data.alerts.map((a, i) => (
            <div key={i} className={clsx('px-sm py-xs rounded-md', alertClasses[a.severity])}>
              <Text strong>{a.message}</Text>
            </div>
          ))}
        </div>
      )}

      <Card>
        <Text variant="label" strong>
          Consumo mensal x limite
        </Text>
        <ResponsiveContainer width="100%" height={300} className="mt-md">
          <ComposedChart data={chartData} margin={{ left: 0, right: 16, top: 16, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#e9eff6" />
            <XAxis dataKey="label" stroke="#8897AE" tickMargin={8} fontSize={11} />
            <YAxis stroke="#8897AE" tickFormatter={(v) => `${v}h`} fontSize={11} />
            <Tooltip
              formatter={(value: number, name: string) => [
                fmtHours(value),
                name === 'hoursLimit' ? 'Limite' : 'Consumido',
              ]}
            />
            <Bar dataKey="consumedHours" radius={[4, 4, 0, 0]} maxBarSize={40}>
              {chartData.map((m, i) => (
                <Cell key={i} fill={m.overLimit ? '#ff3838' : '#2FD181'} />
              ))}
            </Bar>
            <Line
              type="step"
              dataKey="hoursLimit"
              stroke="#455468"
              strokeDasharray="5 4"
              dot={false}
              strokeWidth={1.5}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="gap-md mt-xs flex">
          <Legend color="#2FD181" label="Consumo" />
          <Legend color="#ff3838" label="Acima do limite" />
          <Legend dashed label="Limite mensal" />
        </div>
      </Card>

      <Card>
        <Text variant="label" strong className="mb-xs">
          Detalhamento mensal
        </Text>
        <SimpleTable
          headers={['Mês', 'Tickets', 'Consumido', 'Limite', 'Acumulado', 'Limite acumulado']}
          numericFrom={1}
          rows={data.monthly.map((m) => ({
            key: `${m.year}-${m.month}`,
            highlight: m.overLimit,
            cells: [
              monthLabel(m),
              String(m.ticketCount),
              fmtHours(m.consumedHours),
              fmtHours(m.hoursLimit),
              fmtHours(m.cumulativeConsumedHours),
              fmtHours(m.cumulativeLimitHours),
            ],
          }))}
        />
      </Card>

      <div className="gap-md grid grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <Text variant="label" strong className="mb-xs">
            Tickets com mais horas no período
          </Text>
          {data.topTickets.length === 0 ? (
            <Text className="text-neutral-muted">Nenhum ticket no período.</Text>
          ) : (
            <SimpleTable
              headers={['Ticket', 'Título', 'Data', 'Responsável', 'Horas']}
              numericFrom={4}
              rows={data.topTickets.map((tk) => ({
                key: tk.number,
                cells: [
                  tk.number,
                  tk.title,
                  fmtDate(tk.date),
                  tk.userName ?? '-',
                  fmtHours(tk.hours),
                ],
              }))}
            />
          )}
        </Card>
        <Card>
          <Text variant="label" strong className="mb-xs">
            Horas por responsável
          </Text>
          {data.byUser.length === 0 ? (
            <Text className="text-neutral-muted">Nenhum ticket no período.</Text>
          ) : (
            <SimpleTable
              headers={['Responsável', 'Tickets', 'Horas']}
              numericFrom={1}
              rows={data.byUser.map((u) => ({
                key: u.name,
                cells: [u.name, String(u.ticketCount), fmtHours(u.hours)],
              }))}
            />
          )}
        </Card>
      </div>
    </>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text variant="label" size="xs" className="text-neutral-muted uppercase">
        {label}
      </Text>
      <Text strong>{value}</Text>
    </div>
  );
}

function Legend({ color, label, dashed }: { color?: string; label: string; dashed?: boolean }) {
  return (
    <span className="gap-4xs flex items-center">
      {dashed ? (
        <span className="border-neutral-default inline-block w-4 border-t-2 border-dashed" />
      ) : (
        <span className="inline-block size-2.5 rounded-sm" style={{ background: color }} />
      )}
      <Text variant="label" size="xs" className="text-neutral-muted">
        {label}
      </Text>
    </span>
  );
}

function SimpleTable({
  headers,
  rows,
  numericFrom,
}: {
  headers: string[];
  rows: { key: string; cells: string[]; highlight?: boolean }[];
  numericFrom: number;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={h}
                className={clsx(
                  'bg-neutral-soft-surface border-neutral-subtle px-xs py-2xs border-b',
                  i >= numericFrom ? 'text-right' : 'text-left'
                )}
              >
                <Text variant="label" size="xs" strong className="text-neutral-strong uppercase">
                  {h}
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={clsx(r.highlight && 'text-error-default')}>
              {r.cells.map((c, i) => (
                <td
                  key={i}
                  className={clsx(
                    'border-neutral-subtle px-xs py-2xs border-b',
                    i >= numericFrom ? 'text-right whitespace-nowrap' : 'text-left'
                  )}
                >
                  <Text className={clsx(r.highlight && 'text-error-default')}>{c}</Text>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
